import { CallFileType } from "@prisma/client";
import { Limits } from "../vars/Limits";
import { ULogger } from "./ULogger";

export class UFile {
  private static readonly CALL_CONTENT_TYPES = [
    "audio/mpeg",
    "audio/wav",
    "audio/ogg",
    "audio/mp4",
    "audio/aac",
    "audio/x-m4a",
    "audio/flac",
    "video/mp4",
    "video/quicktime",
    "video/webm",
    "video/x-matroska",
    "video/x-msvideo",
    "video/x-m4v",
  ];

  private static readonly CALL_EXTENSIONS = [
    ".mp3",
    ".wav",
    ".ogg",
    ".m4a",
    ".aac",
    ".flac",
    ".mp4",
    ".mov",
    ".webm",
    ".mkv",
    ".avi",
    ".m4v",
  ];

  private static readonly MAX_CALL_FILE_SIZE = Limits.MAX_CALL_FILE_SIZE;
  private static readonly MAX_CALL_FILES_PER_UPLOAD = Limits.MAX_CALL_FILES_PER_UPLOAD;

  public static getCallContentTypes(): string[] {
    return [...UFile.CALL_CONTENT_TYPES];
  }

  public static getCallExtensions(): string[] {
    return [...UFile.CALL_EXTENSIONS];
  }

  public static getCallAccept(): string {
    return [...UFile.CALL_CONTENT_TYPES, ...UFile.CALL_EXTENSIONS].join(",");
  }

  public static getCallFormatsLabel(): string {
    return UFile.CALL_EXTENSIONS.join(", ");
  }

  public static getMaxCallFileSize(): number {
    return UFile.MAX_CALL_FILE_SIZE;
  }

  public static getMaxCallFilesPerUpload(): number {
    return UFile.MAX_CALL_FILES_PER_UPLOAD;
  }

  public static isCallFile(file: File): boolean {
    return (
      UFile.CALL_CONTENT_TYPES.includes(file.type) ||
      UFile.CALL_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext))
    );
  }

  public static isAudioFile(file: File): boolean {
    return (
      file.type.startsWith("audio/") ||
      [".mp3", ".wav", ".ogg", ".m4a", ".aac", ".flac"].some((ext) =>
        file.name.toLowerCase().endsWith(ext),
      )
    );
  }

  public static getCallFileType(file: File): CallFileType {
    if (UFile.isAudioFile(file)) return CallFileType.AUDIO;
    if (UFile.isVideoFile(file)) return CallFileType.VIDEO;
    throw new Error("Invalid file type. Only audio and video are supported.");
  }

  public static getCallFileTypeFromMimeType(mimeType: string): CallFileType {
    if (mimeType.startsWith("audio/")) return CallFileType.AUDIO;
    if (mimeType.startsWith("video/")) return CallFileType.VIDEO;
    return CallFileType.VIDEO; // Default fallback
  }

  public static isVideoFile(file: File): boolean {
    return (
      file.type.startsWith("video/") ||
      [".mp4", ".mov", ".webm", ".mkv", ".avi", ".m4v"].some((ext) =>
        file.name.toLowerCase().endsWith(ext),
      )
    );
  }

  public static formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} Go`;
  }

  public static getNameWithoutExtension(filename: string): string {
    return filename.replace(/\.[^/.]+$/, "");
  }

  public static async download(link: string, filename: string = "file") {
    try {
      const response = await fetch(link);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const linkElement = document.createElement("a");
      linkElement.href = url;
      linkElement.setAttribute("download", filename);
      document.body.appendChild(linkElement);
      linkElement.click();
      document.body.removeChild(linkElement);
      URL.revokeObjectURL(url);
    } catch (error) {
      ULogger.error(UFile.name, "Error downloading file, falling back to window.open", error);
      const linkElement = document.createElement("a");
      linkElement.href = link;
      linkElement.setAttribute("download", filename);
      linkElement.target = "_blank";
      document.body.appendChild(linkElement);
      linkElement.click();
      document.body.removeChild(linkElement);
    }
  }

  public static getExtensionFromMimeType(mimeType: string): string | null {
    const mimeToExt: Record<string, string> = {
      "audio/mpeg": "mp3",
      "audio/mp3": "mp3",
      "audio/wav": "wav",
      "audio/ogg": "ogg",
      "audio/m4a": "m4a",
      "audio/x-m4a": "m4a",
      "audio/aac": "aac",
      "audio/flac": "flac",
      "audio/webm": "webm",
      "video/mp4": "mp4",
      "video/mpeg": "mpeg",
      "video/quicktime": "mov",
      "video/x-msvideo": "avi",
      "video/webm": "webm",
      "video/x-matroska": "mkv",
      "video/x-m4v": "m4v",
      "application/pdf": "pdf",
      "text/plain": "txt",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
    };

    const cleanMime = mimeType.split(";")[0].toLowerCase();
    if (mimeToExt[cleanMime]) {
      return mimeToExt[cleanMime];
    }

    if (cleanMime.startsWith("audio/")) {
      return "mp3";
    }

    if (cleanMime.startsWith("video/")) {
      return "mp4";
    }

    return null;
  }
  public static getMimeType(filePath: string): string {
    const ext = filePath.split(".").pop()?.toLowerCase() || "";
    const extToMime: Record<string, string> = {
      mp3: "audio/mp3",
      wav: "audio/wav",
      ogg: "audio/ogg",
      m4a: "audio/m4a",
      "x-m4a": "audio/m4a",
      aac: "audio/aac",
      flac: "audio/flac",
      mp4: "video/mp4",
      webm: "video/webm",
      mov: "video/quicktime",
      mkv: "video/x-matroska",
      avi: "video/x-msvideo",
      m4v: "video/x-m4v",
      pdf: "application/pdf",
      txt: "text/plain",
    };
    return extToMime[ext] || "application/octet-stream";
  }

  public static async getMediaDuration(file: File): Promise<number> {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        const duration = video.duration;
        resolve(duration);
      };

      video.onerror = () => {
        resolve(0);
      };

      video.src = URL.createObjectURL(file);
    });
  }

  public static async getMediaDurationFromUrl(url: string): Promise<number> {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        resolve(video.duration);
      };

      video.onerror = () => {
        resolve(0);
      };

      video.src = url;
    });
  }
}
